import React, { useState, useEffect } from 'react';
import { useTheme } from '../Home/ThemeContext';
import { Menu, X, Sparkles, Moon, Sun, UserPlus, LogIn, GraduationCap, Users, Shield, LayoutDashboard, LogOut } from 'lucide-react';
import logo from '../../img/image.png';
import logo2 from '../../img/logo.png';
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { clearAuthData } from "../../utils/auth";


export default function Header() {
  const { isDarkMode, toggleTheme } = useTheme();
  const { isAuthenticated, logout, user } = useAuth();
  const navigate = useNavigate();


  // Determine dashboard URL based on user type
  const getDashboardUrl = () => {
    const userType = (user?.userType || user?.type || user?.role || '').toLowerCase();
  
    switch (userType) {
      case 'student': return '/student/dashboard';
      case 'partner': return '/partner/dashboard';
      case 'college': return '/college/dashboard';
      default:        return '/student/dashboard';
    }
  };
  
  const getUserTypeLabel = () => {
    const userType = (user?.userType || user?.type || user?.role || '').toLowerCase();
  
    switch (userType) {
      case 'student': return 'Student';
      case 'partner': return 'Partner';
      case 'college': return 'College';
      default:        return '';
    }
  };
  
  const getUserGradient = () => {
    const userType = (user?.userType || user?.type || user?.role || '').toLowerCase();
  
    switch (userType) {
      case 'student': return 'from-blue-500 to-cyan-500';
      case 'partner': return 'from-purple-500 to-pink-500';
      case 'college': return 'from-orange-500 to-red-500';
      default:        return 'from-blue-500 to-cyan-500';
    }
  };
  

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);


  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen || showAuthModal ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen, showAuthModal]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleGetStartedClick = (e) => {
    e.preventDefault();
    setShowAuthModal(true);
  };
  

  const userTypes = [
    {
      type: 'Student',
      icon: GraduationCap,
      description: 'Join as a student to access courses and learning materials',
      signupLink: '/signup?type=student',
      loginLink: '/login',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      type: 'Partner',
      icon: Users,
      description: 'Collaborate with us as a business partner',
      signupLink: '/signup?type=partner',
      loginLink: '/login?type=partner',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      type: 'College',
      icon: Shield,
      description: 'Administrative access for platform management',
      signupLink: '/signup?type=college',
      loginLink: '/login?type=college',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-30 transition-all duration-500 ${
          isScrolled
            ? isDarkMode 
              ? 'bg-gray-900/95 shadow-2xl border-b border-gray-700 backdrop-blur-xl'
              : 'bg-white/95 shadow-lg border-b border-gray-200 backdrop-blur-xl'
            : isDarkMode
              ? 'bg-gray-900/80 backdrop-blur-md'
              : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            
            {/* Logo Section */}
            <div className="flex-shrink-0 group">
              <div className="flex items-center">
                <div className="relative flex items-center gap-1 sm:gap-3">
                  <div
                    className={`absolute inset-0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 ${
                      isDarkMode ? 'bg-gradient-to-r from-blue-500/40 via-purple-500/40 to-pink-500/40' : 'bg-gradient-to-r from-blue-400/30 via-purple-400/30 to-pink-400/30'
                    }`}
                  />

                  <a 
                    href="https://www.vidyazon.com/"
                    className="flex items-center px-2 sm:px-3 py-0.5 sm:py-1.5 rounded-xl transition-all duration-300 group-hover:scale-105 bg-transparent"
                  >
                    <img
                      className="h-8 w-8 sm:h-12 sm:w-12 object-contain relative z-10 transition-transform duration-300 group-hover:rotate-3 mr-1 sm:mr-2"
                      src={logo}
                      alt="Vidyazon Logo"
                    />
                    <span
                      className={`text-lg sm:text-2xl md:text-3xl font-extrabold tracking-tight relative z-10 transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-black'
                      }`}
                    >
                      Vidyazon
                    </span>
                  </a>

                  <div className="relative hidden sm:flex items-center">
                    <div className={`h-10 sm:h-14 w-0.5 relative z-10 transition-all duration-300 ${
                      isDarkMode ? 'bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500' : 'bg-gradient-to-b from-blue-400 via-purple-400 to-pink-400'
                    } group-hover:h-16`} />
                    <div className={`absolute inset-0 w-1 blur-sm ${
                      isDarkMode ? 'bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500' : 'bg-gradient-to-b from-blue-400 via-purple-400 to-pink-400'
                    } opacity-0 group-hover:opacity-70 transition-opacity duration-300`} />
                  </div>

                  <a 
                    href="https://aespleduzone.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`px-1.5 py-1.5 sm:px-2 sm:py-3 rounded-xl transition-all duration-300 ${
                      isDarkMode ? 'bg-white' : 'bg-transparent'
                    } group-hover:scale-105 shadow-sm`}
                  >
                    <img
                      className="h-8 sm:h-12 object-contain relative z-10 transition-transform duration-300 group-hover:rotate-3"
                      src={logo2}
                      alt="Vidyazon Logo"
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex lg:items-center lg:space-x-1">
              {navLinks.map((link, index) => (
                <a
                  key={link.name}
                  href={link.href}
                  className={`relative px-4 py-2 font-semibold transition-colors duration-300 group ${
                    isDarkMode 
                      ? 'text-gray-200 hover:text-blue-400' 
                      : 'text-gray-800 hover:text-blue-600'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {link.name}
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 group-hover:w-full group-hover:left-0 transition-all duration-300" />
                </a>
              ))}
            </nav>
            
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              {/* Theme Toggle Button - Desktop */}
              <button
                onClick={toggleTheme}
                className={`hidden sm:flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl transition-all duration-300 shadow-lg hover:scale-110 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-gray-700 to-gray-800 text-yellow-400 hover:from-gray-600 hover:to-gray-700'
                    : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                }`}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" style={{ animationDuration: '3s' }} />
                ) : (
                  <Moon className="w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </button>

              {/* ✅ Show Dashboard + Logout when authenticated */}
              {isAuthenticated && (
                <div className="hidden lg:flex items-center gap-3">
                  {/* Go to Dashboard Button */}
                  <a
                    href={getDashboardUrl()}
                    className={`inline-flex items-center gap-2 font-bold py-2.5 px-5 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md bg-gradient-to-r ${getUserGradient()} text-white hover:shadow-lg`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>{getUserTypeLabel()} Dashboard</span>
                  </a>

                  {/* Logout Button */}
                  <button
onClick={() => {
  clearAuthData();
  window.location.href = "/";
    navigate("/", { replace: true });
}}                    className={`inline-flex items-center gap-2 font-bold py-2.5 px-5 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md ${
                      isDarkMode
                        ? 'bg-gray-800 text-red-400 hover:bg-gray-700 border border-gray-700'
                        : 'bg-white text-red-500 hover:bg-red-50 border border-red-200'
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}

              {/* ✅ Only show Login & Get Started buttons when NOT authenticated */}
              {!isAuthenticated && (
                <>
                  {/* Login Button with Dropdown - Desktop */}
                  <div className="hidden lg:block relative group">
                    <button
                      className={`inline-flex items-center gap-2 font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md ${
                        isDarkMode
                          ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-700'
                          : 'bg-white text-gray-800 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Login</span>
                    </button>

                    {/* Dropdown Menu on Hover */}
                    <div className={`absolute right-0 mt-2 w-64 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 ${
                      isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                    }`}>
                      <div className="p-3">
                        <p className={`text-xs font-semibold mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Login as:
                        </p>
                        {userTypes.map((user) => {
                          const Icon = user.icon;
                          return (
                            <a
                              key={user.type}
                              href={user.loginLink}
                              className={`flex items-center gap-3 p-3 rounded-lg mb-2 last:mb-0 transition-all duration-200 ${
                                isDarkMode
                                  ? 'hover:bg-gray-700 text-gray-200'
                                  : 'hover:bg-gray-50 text-gray-800'
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${user.gradient} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{user.type}</p>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Get Started Button - Desktop */}
                  <div className="hidden lg:block relative group">
  <button
    onClick={handleGetStartedClick}
    className="relative inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white font-bold py-3 px-8 rounded-full hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 overflow-hidden"
  >
    <span className="relative z-10">Get Started</span>
    <Sparkles className="w-4 h-4 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
  </button>

  {/* Hover Dropdown */}
  {/* Hover Dropdown */}
<div
  className={`absolute right-0 mt-2 w-80 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 ${
    isDarkMode
      ? 'bg-gray-800 border border-gray-700'
      : 'bg-white border border-gray-200'
  }`}
>
  <div className="p-4">
    <p className={`text-sm font-semibold mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
      Get Started As:
    </p>

    {userTypes.map((user) => {
      const Icon = user.icon;
      return (
        <a
          key={user.type}
          href={user.signupLink}
          className={`flex items-center gap-3 p-3 rounded-lg mb-2 last:mb-0 transition-all duration-200 ${
            isDarkMode
              ? 'hover:bg-gray-700 text-gray-200'
              : 'hover:bg-gray-50 text-gray-800'
          }`}
        >
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${user.gradient} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-sm">{user.type}</p>
            <p className="text-xs opacity-70">Create account</p>
          </div>
        </a>
      );
    })}
  </div>
</div>

</div>

                </>
              )}

              {/* Theme Toggle Button - Mobile */}
              <button
                onClick={toggleTheme}
                className={`flex sm:hidden items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 shadow-lg hover:scale-110 relative z-20 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-gray-700 to-gray-800 text-yellow-400 hover:from-gray-600 hover:to-gray-700'
                    : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                }`}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 animate-spin" style={{ animationDuration: '3s' }} />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <button
                  onClick={toggleMenu}
                  className={`inline-flex items-center justify-center p-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 shadow-md relative z-20 ${
                    isDarkMode
                      ? 'text-gray-200 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 bg-gray-800'
                      : 'text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 bg-white'
                  }`}
                >
                  {isMenuOpen ? (
                    <X className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Menu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE SIDEBAR */}
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-500 lg:hidden ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={toggleMenu}
      />
      
      {/* Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-full w-80 z-50 shadow-2xl transition-all duration-500 ease-in-out lg:hidden border-r flex flex-col ${
          isDarkMode 
            ? 'bg-gray-900 border-gray-700' 
            : 'bg-white border-gray-200'
        } ${
          isMenuOpen ? 'transform-none' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
           <a href="https://www.vidyazon.com/" className="flex items-center gap-2">
              <img className="h-12 w-12 object-contain" src={logo} alt="Vidyazon Logo" />
              <span className={`text-xl font-extrabold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Vidyazon
              </span>
           </a>
           <button 
             onClick={toggleMenu} 
             className={`p-2 rounded-full transition-colors ${
               isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
             }`}
           >
              <X className={`h-6 w-6 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} />
           </button>
        </div>

        {/* Sidebar Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <ul className="space-y-2">
            {navLinks.map((link) => (
              <li key={link.name}>
                <a
                  href={link.href}
                  onClick={toggleMenu}
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all duration-200 ${
                    isDarkMode
                      ? 'text-gray-200 hover:bg-gray-800 hover:text-blue-400'
                      : 'text-gray-800 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  {link.name}
                </a>
              </li>
            ))}
          </ul>

          {/* ✅ Show Dashboard card in sidebar when authenticated */}
          {isAuthenticated && (
            <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`rounded-xl p-4 border bg-gradient-to-r ${
                isDarkMode ? 'from-gray-800 to-gray-700 border-gray-600' : 'from-gray-50 to-gray-100 border-gray-200'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getUserGradient()} flex items-center justify-center`}>
                    <LayoutDashboard className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {getUserTypeLabel()} Portal
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {user?.name || user?.email || 'Welcome back!'}
                    </p>
                  </div>
                </div>
                <a
                  href={getDashboardUrl()}
                  onClick={toggleMenu}
                  className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg font-semibold text-sm bg-gradient-to-r ${getUserGradient()} text-white hover:shadow-lg transition-all duration-200 mb-2`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Go to Dashboard
                </a>
                <button
                  onClick={() => { logout(); toggleMenu(); }}
                  className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    isDarkMode
                      ? 'bg-gray-700 text-red-400 hover:bg-gray-600'
                      : 'bg-white text-red-500 hover:bg-red-50 border border-red-200'
                  }`}
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* ✅ Only show portal cards in sidebar when NOT authenticated */}
          {!isAuthenticated && (
            <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="space-y-3">
                {/* Student Portal */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-700 dark:text-blue-300">Student Portal</h3>
                      <p className="text-xs text-blue-600 dark:text-blue-400">Access courses & applications</p>
                    </div>
                  </div>
                  <a
                    href="/login"
                    onClick={toggleMenu}
                    className="block w-full text-center py-2 px-4 rounded-lg font-semibold text-sm bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-lg transition-all duration-200"
                  >
                    Login as Student
                  </a>
                </div>

                {/* Partner Portal */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-green-700 dark:text-green-300">Partner Portal</h3>
                      <p className="text-xs text-green-600 dark:text-green-400">Counselors & institutions</p>
                    </div>
                  </div>
                  <a
                    href="/login?type=partner"
                    onClick={toggleMenu}
                    className="block w-full text-center py-2 px-4 rounded-lg font-semibold text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg transition-all duration-200"
                  >
                    Login as Partner
                  </a>
                </div>
              </div>
            </div>
          )}
        </nav>

        {/* ✅ Only show "Get Started" footer CTA when NOT authenticated */}
        {!isAuthenticated && (
          <div className={`p-4 border-t ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              onClick={handleGetStartedClick}
              className="flex items-center justify-center gap-2 w-full text-center bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white font-bold py-3.5 px-6 rounded-xl hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg shadow-blue-500/30"
            >
              <span>Get Started</span>
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowAuthModal(false)}
          />
          
          <div className={`relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          }`}>
            {/* Modal Header */}
            <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
              isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div>
                <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Welcome to Vidyazon
                </h2>
                <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Choose your account type to continue
                </p>
              </div>
              <button
                onClick={() => setShowAuthModal(false)}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
              >
                <X className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {userTypes.map((user) => {
                  const Icon = user.icon;
                  return (
                    <div
                      key={user.type}
                      className={`rounded-xl p-6 border-2 transition-all duration-300 hover:scale-105 ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-700 hover:border-blue-500'
                          : 'bg-gray-50 border-gray-200 hover:border-blue-500'
                      }`}
                    >
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${user.gradient} flex items-center justify-center mb-4`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      
                      <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {user.type}
                      </h3>
                      
                      <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {user.description}
                      </p>

                      <div className="space-y-3">
                        <a
                          href={user.signupLink}
                          className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 bg-gradient-to-r ${user.gradient} text-white hover:shadow-lg`}
                        >
                          <UserPlus className="w-4 h-4" />
                          <span>Sign Up</span>
                        </a>
                        
                        <a
                          href={user.loginLink}
                          className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 ${
                            isDarkMode
                              ? 'bg-gray-700 text-white hover:bg-gray-600'
                              : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          <LogIn className="w-4 h-4" />
                          <span>Login</span>
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Login Section */}
              <div className={`mt-8 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-center text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already have an account? Quick login:
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {userTypes.map((user) => (
                    <a
                      key={user.type}
                      href={user.loginLink}
                      className={`inline-flex items-center gap-2 py-2 px-5 rounded-full font-semibold transition-all duration-300 hover:scale-105 ${
                        isDarkMode
                          ? 'bg-gray-800 text-white hover:bg-gray-700'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      <LogIn className="w-4 h-4" />
                      <span>{user.type} Login</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}